import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Box,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';

interface DashboardStats {
  entries: {
    total: number;
    completed: number;
    pending: number;
  };
  validation: {
    runs_total: number;
    runs_completed: number;
    runs_failed: number;
    success_rate: number;
  };
  ground_truth: {
    signatures_total: number;
    entries_with_gt: number;
    coverage_percent: number;
  };
  indexing: {
    projects_indexed: number;
    references_total: number;
  };
  active_jobs: Array<{
    job_id: string;
    job_type: string;
    processed: number;
    total: number;
    percent: number;
  }>;
}

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'success' | 'info';
  icon?: string;
}

const StatCardWrapper = styled(Card)<{ color?: string }>(({ theme, color = 'primary' }) => {
  const paletteColor = theme.palette[color as keyof typeof theme.palette] as any;
  const borderColor = paletteColor?.main || theme.palette.primary.main;
  
  return {
    height: '100%',
    borderLeft: `4px solid ${borderColor}`,
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8],
    },
  };
});

function StatCard({ title, value, subtitle, color = 'primary', icon }: StatCardProps) {
  return (
    <StatCardWrapper color={color}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="body2" color="text.secondary" fontWeight="600">
            {title}
          </Typography>
          {icon && <Box fontSize="1.5rem">{icon}</Box>}
        </Box>
        
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </StatCardWrapper>
  );
}

export default function StatsCards() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/stats/dashboard');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setStats(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !stats) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ height: 140 }}>
              <CardContent>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="80%" height={48} sx={{ mt: 2 }} />
                <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (error) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" color="error">Failed to load statistics</Typography>
        <Typography variant="body2" color="text.secondary">{error}</Typography>
      </Box>
    );
  }

  if (!stats) return null;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard title="Total Entries" value={stats.entries.total} subtitle={`${stats.entries.completed} completed`} color="primary" icon="📊" />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard title="Validation Runs" value={stats.validation.runs_total} subtitle={`${stats.validation.success_rate}% success`} color={stats.validation.success_rate > 80 ? 'success' : 'warning'} icon="✅" />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard title="GT Coverage" value={`${stats.ground_truth.coverage_percent}%`} subtitle={`${stats.ground_truth.signatures_total} signatures`} color="info" icon="🎯" />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard title="Active Jobs" value={stats.active_jobs.length} subtitle={stats.active_jobs.length > 0 ? 'Processing' : 'Idle'} color={stats.active_jobs.length > 0 ? 'warning' : 'secondary'} icon="⚙️" />
      </Grid>
    </Grid>
  );
}
